import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError } from '../../../lib/apiError';
import { orgUnitTypesApi } from './organizationApi';
import type { OrgUnitTypeOption } from './organizationTypes';

/**
 * The tier ladder, for a chart to draw its columns against.
 *
 * Fetched, because **the columns are the ladder rather than what happens to be in it**: a
 * tier no unit carries still gets a column, and an empty column is the point — it is what a
 * skipped level looks like. Deriving the columns from the units on screen made them appear
 * and vanish as records moved, so a skipped level was never visible.
 *
 * Unpaged: the options feed is the whole ladder by construction, which is also why it is the
 * feed that carries each tier's level.
 */
export function useTierLadder(reloadKey = 0) {
  const [tiers, setTiers] = useState<OrgUnitTypeOption[]>([]);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    orgUnitTypesApi
      .options(controller.signal)
      .then(setTiers)
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(
          caught instanceof ApiError ? caught : new ApiError('The tiers could not be loaded.', 0),
        );
      });

    return () => controller.abort();
  }, [reloadKey]);

  /*
    The lane list `<TreeChart>` wants: the key it matches a unit against, and the label for
    the column head. Memoised because the chart takes it as a prop and re-resolves its lanes
    whenever the reference changes.
  */
  const lanes = useMemo(
    () => tiers.map((tier) => ({ key: tier.id, label: tier.name })),
    [tiers],
  );

  /** The tier a column stands for, for its own actions. */
  const tierAt = useCallback(
    (key: string) => tiers.find((tier) => tier.id === key) ?? null,
    [tiers],
  );

  return { tiers, lanes, tierAt, error };
}
